import { zodResolver } from "@hookform/resolvers/zod"
import type { TFunction } from "i18next"
import { AlertTriangleIcon, Loader2 } from "lucide-react"
import { useForm, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"
import { usePermission } from "@/contexts/permission"
import { toast } from "@/hooks/use-toast"
import { cn } from "@/lib/utils"

const REVERSAL_MODE_NORMAL = "normal"
const REVERSAL_MODE_FORCED = "forced"

const REVERSAL_MODES = [
  REVERSAL_MODE_NORMAL,
  REVERSAL_MODE_FORCED,
] as const

const REVERSAL_MODE_CONFIG = {
  [REVERSAL_MODE_NORMAL]: {
    labelKey: "common.reversal.modeNormal",
    hintKey: "common.reversal.modeNormalHint",
    dot: "bg-blue-500",
    ring: "ring-blue-500",
    card: "border-blue-200 bg-blue-50",
  },
  [REVERSAL_MODE_FORCED]: {
    labelKey: "common.reversal.modeForced",
    hintKey: "common.reversal.modeForcedHint",
    dot: "bg-red-500",
    ring: "ring-red-500",
    card: "border-red-200 bg-red-50",
  },
} as const

/**
 * As duas confirmações só valem no modo forçado — é lá que a reversão é registrada mesmo com o
 * banco recusando a baixa, e quem afirma que o título não pode mais ser baixado é o operador.
 */
const createReversalSchema = (t: TFunction) =>
  z
    .object({
      mode: z.enum([
        REVERSAL_MODE_NORMAL,
        REVERSAL_MODE_FORCED,
      ]),
      reason: z.string().min(1, {
        message: t("common.reversal.reasonRequired"),
      }),
      bankCannotSettle: z.boolean(),
      understandsIrreversible: z.boolean(),
    })
    .refine(
      (data) =>
        data.mode !== REVERSAL_MODE_FORCED ||
        data.bankCannotSettle,
      {
        path: ["bankCannotSettle"],
        message: t("common.reversal.confirmBankRequired"),
      }
    )
    .refine(
      (data) =>
        data.mode !== REVERSAL_MODE_FORCED ||
        data.understandsIrreversible,
      {
        path: ["understandsIrreversible"],
        message: t(
          "common.reversal.confirmIrreversibleRequired"
        ),
      }
    )

type ReversalSchemaProps = z.infer<
  ReturnType<typeof createReversalSchema>
>

const DEFAULT_VALUES: ReversalSchemaProps = {
  mode: REVERSAL_MODE_NORMAL,
  reason: "",
  bankCannotSettle: false,
  understandsIrreversible: false,
}

export interface ReversalConfirmation {
  reason: string
  forced: boolean
}

interface ReversalDialogProps {
  /** Botão que abre o diálogo. Sem ele, o diálogo é controlado por `open`/`setOpen`. */
  trigger?: React.ReactNode
  open?: boolean
  setOpen?: (open: boolean) => void
  title: string
  description: string
  successMessage: string
  successForcedMessage: string
  /** Rejeitar aqui mantém o diálogo aberto e avisa o operador. */
  onConfirm: (confirmation: ReversalConfirmation) => Promise<unknown>
}

/**
 * O diálogo de reversão — de pedido ou de ciclo de assinatura.
 *
 * <p>Existe compartilhado porque a parte que não pode divergir entre os dois é justamente a de
 * segurança: o modo forçado só para staff Pay2B, as duas confirmações explícitas e o motivo
 * obrigatório. Duplicar isso é convidar um dos dois a ficar para trás.
 */
export const ReversalDialog = ({
  trigger,
  open,
  setOpen,
  title,
  description,
  successMessage,
  successForcedMessage,
  onConfirm,
}: ReversalDialogProps) => {
  const { t } = useTranslation()
  const { isSuperAdmin } = usePermission()

  const form = useForm<ReversalSchemaProps>({
    resolver: zodResolver(createReversalSchema(t)),
    defaultValues: DEFAULT_VALUES,
    mode: "onBlur",
    reValidateMode: "onChange",
  })

  const mode = useWatch({
    control: form.control,
    name: "mode",
  })
  const isForced = mode === REVERSAL_MODE_FORCED

  // Fechar sem confirmar não pode deixar `forced` e as duas confirmações marcadas esperando a
  // próxima abertura — a ação é destrutiva e a reabertura viria pré-armada.
  const handleOpenChange = (isOpen: boolean) => {
    setOpen?.(isOpen)
    if (!isOpen) form.reset(DEFAULT_VALUES)
  }

  const handleSubmit = form.handleSubmit(
    async (data) => {
      const forced = data.mode === REVERSAL_MODE_FORCED
      try {
        await onConfirm({ reason: data.reason, forced })
        toast({
          title: forced
            ? successForcedMessage
            : successMessage,
          variant: "success",
        })
        handleOpenChange(false)
      } catch {
        // O `handleSubmit` do react-hook-form re-lança o que o onSubmit rejeitar; sem o catch a
        // reversão que falha vira unhandled rejection e o diálogo some do radar do operador.
        toast({
          title: t("common.reversal.error"),
          variant: "destructive",
        })
      }
    },
    (errors) => {
      const [firstInvalidField] = Object.keys(errors)
      if (firstInvalidField) {
        form.setFocus(
          firstInvalidField as keyof ReversalSchemaProps
        )
      }
    }
  )

  const formIsPending = form.formState.isSubmitting

  return (
    <AlertDialog onOpenChange={handleOpenChange} open={open}>
      {trigger && (
        <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      )}
      <AlertDialogContent className="max-h-[90vh] overflow-y-auto">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-center text-xl">
            {title}
          </AlertDialogTitle>
          <Separator />
          <AlertDialogDescription>
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-4">
            {isSuperAdmin && (
              <FormField
                control={form.control}
                name="mode"
                render={({ field }) => (
                  <FormItem className="space-y-3">
                    <FormLabel>
                      {t("common.reversal.modeLabel")}
                    </FormLabel>
                    <FormControl>
                      <div
                        aria-label={t(
                          "common.reversal.modeLabel"
                        )}
                        className="flex flex-wrap gap-3"
                        role="radiogroup"
                      >
                        {REVERSAL_MODES.map((option) => {
                          const config =
                            REVERSAL_MODE_CONFIG[option]
                          const isSelected =
                            field.value === option
                          return (
                            <button
                              aria-checked={isSelected}
                              className={cn(
                                "flex min-w-[130px] flex-1 flex-col gap-1 rounded-xl border-2 p-3 text-left transition-all",
                                config.card,
                                isSelected
                                  ? `${config.ring} ring-2`
                                  : "opacity-50 hover:opacity-80"
                              )}
                              key={option}
                              onClick={() =>
                                field.onChange(option)
                              }
                              role="radio"
                              type="button"
                            >
                              <div className="flex items-center gap-1.5">
                                <span
                                  className={cn(
                                    "h-2 w-2 rounded-full",
                                    config.dot
                                  )}
                                />
                                <span className="text-sm font-semibold">
                                  {t(config.labelKey)}
                                </span>
                              </div>
                              <p className="text-[11px] text-muted-foreground">
                                {t(config.hintKey)}
                              </p>
                            </button>
                          )
                        })}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {isForced && (
              <p
                className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700"
                role="alert"
              >
                <AlertTriangleIcon
                  aria-hidden
                  className="mt-0.5 h-4 w-4 shrink-0"
                />
                {t("common.reversal.warning")}
              </p>
            )}
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("common.reversal.reasonLabel")}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t(
                        "common.reversal.reasonPlaceholder"
                      )}
                      className="resize-none text-sm"
                      rows={4}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {isForced && (
              <>
                <FormField
                  control={form.control}
                  name="bankCannotSettle"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>
                            {t(
                              "common.reversal.confirmBank"
                            )}
                          </FormLabel>
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="understandsIrreversible"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>
                            {t(
                              "common.reversal.confirmIrreversible"
                            )}
                          </FormLabel>
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}
            <Separator />
            <AlertDialogFooter>
              <AlertDialogCancel disabled={formIsPending}>
                {t("common.cancel")}
              </AlertDialogCancel>
              <Button
                type="submit"
                variant="destructive"
                disabled={formIsPending}
              >
                {formIsPending && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                {t(
                  isForced
                    ? "common.reversal.confirmActionForced"
                    : "common.reversal.confirmAction"
                )}
              </Button>
            </AlertDialogFooter>
          </form>
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  )
}
